import {Amplify} from 'aws-amplify'
import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signOut,
  signUp,
  updatePassword,
} from 'aws-amplify/auth'
import {cognitoUserPoolsTokenProvider} from 'aws-amplify/auth/cognito'
import {
  sharedInMemoryStorage as CognitoInMemoryStorage,
  defaultStorage as CognitoLocalStorage,
} from 'aws-amplify/utils'

import {hasLocalStorage} from './local-storage'

export const init = (amplifyConfig: Record<string, unknown>) => {
  Amplify.configure(amplifyConfig)

  // We use the localStorage as default storage mechanism. In case it is not available or fails, we use a memory storage as a fallback.
  const cognitoStorage = hasLocalStorage ? CognitoLocalStorage : CognitoInMemoryStorage
  cognitoUserPoolsTokenProvider.setKeyValueStorage(cognitoStorage)
}

export const getCurrentAuthUser = async () => getCurrentUser()

export const getUserAttributes = () => fetchUserAttributes()

export const getCurrentSession = async () => {
  const authSession = await fetchAuthSession()
  return authSession.tokens?.accessToken.toString()
}

export const authSignUp = (username: string, password: string) =>
  signUp({
    username,
    password,
    options: {
      userAttributes: {
        email: username, // username is an email
      },
    },
  })

export const authConfirmSignUp = (username: string, confirmationCode: string) =>
  confirmSignUp({
    username,
    confirmationCode,
  })

export const authResendSignUpCode = (username: string) => resendSignUpCode({username})

export const authSignIn = (email: string, password: string) => {
  const input = {
    username: email,
    password,
  }
  return signIn(input)
}

export const forgotPassword = (email: string) => resetPassword({username: email})

export const setNewPassword = (email: string, authCode: string, password: string) =>
  confirmResetPassword({
    username: email,
    confirmationCode: authCode,
    newPassword: password,
  })

export const completeNewPassword = (newPassword: string) =>
  confirmSignIn({challengeResponse: newPassword})

export const changePassword = (oldPassword: string, newPassword: string) =>
  updatePassword({oldPassword, newPassword})

export const authSignOut = (global = false) => signOut({global})

export const onAuthFailAfterVisibilityChange = (onFail: () => void) => {
  document.addEventListener('visibilitychange', async () => {
    // Check if auth chunk has already been loaded otherwise the user was never logged in and we don't have to load it now.
    if (document.visibilityState === 'visible' && !(await getCurrentSession())) {
      onFail()
    }
  })
}
